import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm, useFieldArray } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { usePaymentInvoiceReturnRefundDetailsAPI } from "@/app/hooks/api_hooks/payment/usePaymentInvoiceReturnRefundDetailsAPI";
import { BiRupee } from "react-icons/bi";
import { IRefundInvoiceProps } from "@/api_framework/api_modals/payment";
import { IInvoiceProps, useCartInvoiceListAPI } from "@/app/hooks/api_hooks/Cart/useCartInvoiceListAPI";
import { ICartState, RESET_INVOICE_CART_SLICE } from "@/app_redux/reducers/slices/auth/cart_slice";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { toNumber } from "lodash";
import { usePaymentUserReturnedAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserReturnedAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useDispatch } from "react-redux";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  setInitialState?: (value: boolean) => void;
  invoiceId: string;
  invoiceType: string;
  source: string;
};

const InvoiceReturnModalComponent: React.FC<Props> = ({
  open,
  setOpen,
  setInitialState,
  invoiceId,
  invoiceType,
  source,
}) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();

  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { invoiceListFilter } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;

  const { execute: fetchInvoiceReturnRefund, invoiceDetailsData } = usePaymentInvoiceReturnRefundDetailsAPI();
  const { execute: returnedInvoice } = usePaymentUserReturnedAPI();
  const { execute: fetchInvoiceList } = useCartInvoiceListAPI();

  const [loader, setLoader] = useState<boolean>(false);
  const dispatch = useDispatch();

  //Default State
  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  //FromHook
  const { control, handleSubmit, formState } = formHook;
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { fields: productInfo, append: updateProducts } = useFieldArray({
    control,
    name: "products",
  });

  const onSubmit = (data) => {
    const formdata = data?.products;

    let isValidData = true;

    const updatedPetWiseItems = invoiceDetailsData?.pet_wise_items?.map((pet) => {
      return {
        ...pet,
        items: pet?.items
          .map((item) => {
            const matchingData = formdata?.find((dataItem) => dataItem.unique_row_id === item.unique_row_id);
            if (parseFloat(matchingData.qty_to_return) > matchingData?.pending_qty_to_return) {
              isValidData = false;
            }
            if (matchingData && parseFloat(matchingData.qty_to_return) !== 0) {
              return {
                ...item,
                qty_to_return: parseFloat(matchingData.qty_to_return),
              };
            }
            return null;
          })
          .filter((item) => item !== null),
      };
    });
    if (!isValidData) return;
    setLoader(true);

    const constructedBody: IRefundInvoiceProps = {
      invoice_id: invoiceId,
      clinic_id: mappedClinicId,
      return_items: updatedPetWiseItems as any,
    };
    returnedInvoice(constructedBody).then(({ status }) => {
      setLoader(false);
      if (status) {
        if (source === "payment_page") {
          setLoader(false);
          dispatch(RESET_INVOICE_CART_SLICE());
          const constructedBody: IInvoiceProps = {
            user_id: customerId,
            start_date: null,
            end_date: null,
            statuses: [],
          };

          window.location.reload();
          // fetchInvoiceList(constructedBody, "payment_page").then(({ stat }) => {
          //   if (stat) {
          //     setOpen(false);
          //     setInitialState(false);
          //   } else {
          //     setOpen(false);
          //     setInitialState(false);
          //   }
          // });
        } else {
          fetchUserWalletDetails(customerId);
          setLoader(false);
          const constructedBodyFilter: IInvoiceProps = {
            user_id: invoiceListFilter?.user_id,
            start_date: invoiceListFilter?.start_date,
            end_date: invoiceListFilter?.end_date,
            statuses: invoiceListFilter.statuses,
          };
          fetchInvoiceList(constructedBodyFilter, source);
          setOpen(false);
        }
      }
    });
  };

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceReturnRefund(invoiceId, "return");
    }
  }, [invoiceId]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (invoiceDetailsData?.pet_wise_items?.length > 0 && !hasInitialized.current) {
      const allItems = invoiceDetailsData?.pet_wise_items?.flatMap((petWiseItem) => petWiseItem.items);
      updateProducts(allItems.map((item) => ({ ...item, qty_to_return: 0 })));
      hasInitialized.current = true;
    }
  }, [invoiceDetailsData, hasInitialized.current]);

  const totalAmountToPaid = () => {
    const products = formHook.watch("products"); // Watching the 'products' field

    if (!Array.isArray(products)) {
      // Safeguard in case 'products' is not an array
      return { totalToBePaid: 0 };
    }

    const totals = products.reduce(
      (acc, item) => {
        const qtyToReturn = item?.qty_to_return ?? 0;
        const net_price_per_qty = item?.net_price_per_qty ?? 0;

        if (qtyToReturn > 0) {
          acc.totalToBePaid += qtyToReturn * net_price_per_qty;
        }

        return acc;
      },
      { totalToBePaid: 0 },
    );

    return totals;
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
            formHook.reset();
            hasInitialized.current = false;
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-6xl p-6"
              >
                <div className="flex items-center justify-between border-b pb-1">
                  <div>
                    <h2 className="text-xs font-semibold ">{invoiceType} Request</h2>
                    <p className="text-xs text-[#808DA0] pt-1">{invoiceId}</p>
                  </div>
                  <p>
                    <XMarkIcon
                      className="h-4 w-4 cursor-pointer font-bold"
                      onClick={() => {
                        hasInitialized.current = false;
                        formHook.reset();
                        setOpen(false);
                      }}
                    />
                  </p>
                </div>
                <div className="my-3">
                  <p className="text-sm font-medium my-2">Select items for {invoiceType}</p>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F4F8FF]">
                        <th className="p-3 text-xs font-medium">Product/Service</th>
                        <th className="p-3 text-xs font-medium">Lot Details</th>
                        <th className="p-3 text-xs font-medium">Ordered Qty</th>
                        <th className="p-3 text-xs font-medium">Unit Price</th>
                        <th className="p-3 text-xs font-medium">Net Price</th>
                        <th className="p-3 text-xs font-medium">Net Price Per Qty</th>
                        <th className="p-3 text-xs font-medium">Return Qty</th>
                        <th className="p-3 text-xs font-medium">Return Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productInfo?.map((item: any, index: number) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-3 py-4 text-xs break-words w-56">
                            <div>{formHook.watch(`products.${index}.name`)}</div>
                            <div className="text-green-400">{formHook.watch(`products.${index}.pet_name`)}</div>
                          </td>
                          <td className="p-3 py-4 text-xs">
                            {Object.keys(formHook.watch(`products.${index}.lot_details`))?.length === 0 ? (
                              <span>NA</span>
                            ) : (
                              <>
                                {" "}
                                <span>
                                  {formHook.watch(`products.${index}.lot_details.vetbuddy_lot_number`)},
                                  {formHook.watch(`products.${index}.lot_details.vetbuddy_manufacturer_name`)}
                                </span>
                                <span className="flex items-center">
                                  {formHook.watch(`products.${index}.lot_details.expiry_date`)} ,{" "}
                                  <span className="flex items-center">
                                    <BiRupee className="w-4 h-4" />{" "}
                                    {formHook.watch(`products.${index}.lot_details.vetbuddy_mrp`)}
                                  </span>
                                </span>
                              </>
                            )}
                          </td>
                          <td className="p-3 py-4 text-xs ">
                            {formHook.watch(`products.${index}.qty`)}
                            {formHook.watch(`products.${index}.already_returned_qty`) > 0 && (
                              <p className="text-[#CB9A07]">
                                {" "}
                                Already Returned: {formHook.watch(`products.${index}.already_returned_qty`)}
                              </p>
                            )}
                          </td>
                          <td className="p-3 py-4 text-xs">{formHook.watch(`products.${index}.unit_price`)}</td>
                          <td className="p-3 py-4 text-xs ">{formHook.watch(`products.${index}.net_price`)}</td>
                          <td className="p-3 py-4 text-xs ">
                            {formHook.watch(`products.${index}.net_price_per_qty`) || "NA"}
                          </td>

                          <td
                            className={`p-3 py-4 text-xs ${formHook.watch(`products.${index}.is_return_eligible`) && "w-32"}`}
                          >
                            {formHook.watch(`products.${index}.is_return_eligible`) ? (
                              <div className="-mt-1">
                                <InputWithStartAndEndAddOn
                                  inputClassName="min-m-10"
                                  disabled={
                                    formHook.watch(`products.${index}.pending_qty_to_return`) === 0 ? true : false
                                  }
                                  startAddon="-"
                                  startAddOnCallback={() => {
                                    const currentQuantity = formHook.watch(`products.${index}.qty_to_return`) ?? 1;
                                    if (currentQuantity > 0) {
                                      formHook.setValue(`products.${index}.qty_to_return`, currentQuantity - 1);
                                    }
                                  }}
                                  step={1}
                                  endAddon="+"
                                  endAddOnCallback={() => {
                                    const currentQuantity = parseInt(
                                      formHook.watch(`products.${index}.qty_to_return`) ?? "0",
                                      10,
                                    );
                                    const orderedQty = parseInt(
                                      formHook.watch(`products.${index}.pending_qty_to_return`) ?? "0",
                                      10,
                                    );

                                    if (currentQuantity >= orderedQty) {
                                      return `Amount cannot exceed`;
                                    }

                                    if (!currentQuantity) {
                                      formHook.setValue(`products.${index}.qty_to_return`, currentQuantity + 1);
                                    } else {
                                      const currentQuantity = formHook.watch(`products.${index}.qty_to_return`) ?? 1;
                                      formHook.setValue(`products.${index}.qty_to_return`, currentQuantity + 1);
                                    }
                                  }}
                                  // min={1}
                                  value={formHook.watch(`products.${index}.qty_to_return`) ?? 1}
                                  // onBlur={(e) => {
                                  //   const value = toNumber(e.target.value);
                                  //   if (!value || isNaN(value)) {
                                  //     // Reset to default value if the input is invalid
                                  //     formHook.setValue(`products.${index}.qty_to_return`, 1);
                                  //   }
                                  // }}
                                  onChange={(e: any) => {
                                    const inputValue = e.target.value;

                                    // Allow empty input temporarily
                                    if (inputValue === "") {
                                      formHook.setValue(`products.${index}.qty_to_return`, 0 as any);
                                      return;
                                    }

                                    const value = toNumber(inputValue);
                                    if (!Number.isInteger(value)) {
                                      return; // Prevent setting non-integer values
                                    }
                                    // Validate the amount is less than or equal to `dt`

                                    // Validate numeric input and set value
                                    if (!isNaN(value) && value > 0) {
                                      formHook.setValue(`products.${index}.qty_to_return`, value);
                                    }
                                    // if (parseFloat(formHook.watch(`products.${index}.qty_to_return`)) > formHook.getValues(`products.${index}.ordered_qty`)) {
                                    //   return `Amount cannot exceed`;
                                    // }
                                  }}
                                  type="number"
                                />
                                <p className="text-red-600 text-[9px] absolute font-medium ">
                                  {parseFloat(formHook.watch(`products.${index}.qty_to_return`)) >
                                    formHook.getValues(`products.${index}.pending_qty_to_return`) && "Limit Reached!"}
                                </p>
                              </div>
                            ) : (
                              <p className="">Non-Returnable</p>
                            )}
                          </td>
                          {formHook.watch(`products.${index}.is_return_eligible`) ? (
                            <td className="p-3 py-4 text-xs ">
                              {(
                                parseFloat(formHook.watch(`products.${index}.qty_to_return`) || 0) *
                                parseFloat(formHook.watch(`products.${index}.net_price_per_qty`) || 0)
                              )?.toFixed(2)}
                            </td>
                          ) : (
                            <td className="p-3 py-4 text-xs ">NA</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-[#F2F2F2]">
                  <p className="text-sm font-medium p-3 shadow-sm">
                    Total {invoiceType} Amount:{" "}
                    {productInfo?.length > 0 && totalAmountToPaid()?.totalToBePaid?.toFixed(2)}{" "}
                  </p>
                </div>
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      formHook.reset();
                      hasInitialized.current = false;
                    }}
                    className="rounded-md bg-[#72767f1a] px-4 py-2 text-sm text-[#000000] font-medium "
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`rounded-md ${totalAmountToPaid()?.totalToBePaid === 0 ? "bg-gray-400" : "bg-[#193A6A]"} font-bold px-4 py-2 text-sm text-white `}
                    disabled={totalAmountToPaid()?.totalToBePaid === 0}
                  >
                    {loader ? (
                      <div className="">
                        {" "}
                        <CircleSpinnerLoader h="20" w="20" />
                      </div>
                    ) : (
                      <span>{invoiceType} Invoice</span>
                    )}
                  </button>
                </div>
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default InvoiceReturnModalComponent;
